import { defineStore } from "pinia";
import { gql } from "@apollo/client/core";
import { provideApolloClient, useMutation } from "@vue/apollo-composable";
import { apolloClient } from "@/libs/apollo";
import { platform } from "@/libs/environment";
import { useCordovaStore } from "@/stores/cordova";

provideApolloClient(apolloClient);

const { mutate: login } = useMutation(
  gql`
    mutation AuthLogin($password: String!, $email: String!) {
      authLogin(password: $password, email: $email) {
        userId
        name
        isAdmin
        state
        token
        expiresAt
      }
    }
  `,
  {
    fetchPolicy: "no-cache",
  }
);

export const useAuthStore = defineStore({
  id: "auth",
  state: () => {
    const localStorageAuthIsAdmin = localStorage.getItem("auth_is_admin");
    const localStorageAuthExpiresAt = localStorage.getItem("auth_expires_at");
    const state: {
      _userId?: string;
      _name?: string;
      _isAdmin: boolean;
      _state?: string;
      _token?: string;
      _expiresAt?: Date;
    } = {
      _userId: localStorage.getItem("auth_user_id") || undefined,
      _name: localStorage.getItem("auth_name") || undefined,
      _isAdmin:
        (localStorageAuthIsAdmin && localStorageAuthIsAdmin === "true") ||
        false,
      _state: localStorage.getItem("auth_state") || undefined,
      _token: localStorage.getItem("auth_token") || undefined,
      _expiresAt:
        (localStorageAuthExpiresAt && new Date(localStorageAuthExpiresAt)) ||
        undefined,
    };
    return state;
  },
  getters: {
    isLoggedIn: (state) =>
      !!(
        state._token &&
        state._expiresAt &&
        state._expiresAt.valueOf() > new Date().valueOf()
      ),
    userId: (state) => state._userId,
    name: (state) => state._name,
    state: (state) => state._state,
    isAdmin: (state) => state._isAdmin,
    token: (state) => state._token,
    expiresAt: (state) => state._expiresAt,
  },
  actions: {
    async login(email: string, password: string) {
      const result = await login({ email, password });
      this._userId = result?.data.authLogin.userId as string;
      this._name = result?.data.authLogin.name as string;
      this._state = result?.data.authLogin.state as string;
      this._isAdmin = result?.data.authLogin.isAdmin as boolean;
      this._token = result?.data.authLogin.token as string;
      this._expiresAt = new Date(result?.data.authLogin.expiresAt);
      if (this._userId) {
        localStorage.setItem("auth_user_id", this._userId);
      }
      if (this._name) {
        localStorage.setItem("auth_name", this._name);
      }
      if (this._state) {
        localStorage.setItem("auth_state", this._state);
      }
      localStorage.setItem("auth_is_admin", this._isAdmin.toString());
      localStorage.setItem("auth_token", this._token);
      localStorage.setItem("auth_expires_at", this._expiresAt.toISOString());

      if (platform !== "web") {
        const cordovaStore = useCordovaStore();
        cordovaStore.initCordovaPluginPurchase();
      }
    },
    async logout() {
      this._userId = undefined;
      this._name = undefined;
      this._state = undefined;
      this._isAdmin = false;
      this._token = undefined;
      this._expiresAt = undefined;
      localStorage.removeItem("auth_id");
      localStorage.removeItem("auth_name");
      localStorage.removeItem("auth_state");
      localStorage.removeItem("auth_is_admin");
      localStorage.removeItem("auth_token");
      localStorage.removeItem("auth_expires_at");
      localStorage.clear();
      await apolloClient.clearStore();
    },
    setName(userName: string) {
      this._name = userName;
      localStorage.setItem("auth_name", this._name);
    },
    setState(stateAbbr: string) {
      this._state = stateAbbr;
      localStorage.setItem("auth_state", this._state);
    },
  },
});
